import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Table,
  Spin,
  Tooltip,
  Button,
  Tag,
  Space,
  Typography,
  Select,
  Progress,
  DatePicker,
} from "antd";
import {
  LoadingOutlined,
  CloudDownloadOutlined,
  DownloadOutlined,
  ShopOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
  FilterCriteria,
  ToolbarAction,
} from "../../Common/Layout";
import { IInvoiceAdminListItem } from "../../Services/InvoiceExtractionService";
import { useInvoiceExtraction } from "./useInvoiceExtraction";
import { IPractices } from "../../Context/PracticeContext";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export interface InvoiceExtractionProps {
  onSwitch?: (type: "xcelify" | "invoice") => void;
}

export const InvoiceExtraction: React.FC<InvoiceExtractionProps> = ({ onSwitch }) => {
  const navigate = useNavigate();

  const {
    loading,
    downloading,
    downloadingId,
    allPracticeIds,
    practiceMap,
    companyList,
    loadingCompanies,
    selectedPracticeId,
    selectedClientId,
    statusFilter,
    fromDate,
    toDate,
    items,
    totalItems,
    currentPage,
    pageSize,
    selectedRowKeys,
    extractionProgress,
    setCurrentPage,
    setPageSize,
    handlePracticeChange,
    handleCompanyChange,
    handleStatusChange,
    handleDateRangeChange,
    handleSearch,
    handleRefresh,
    handleRowSelectionChange,
    handleSingleDownload,
    handleBulkExtraction,
    handleCancelExtraction,
  } = useInvoiceExtraction();

  const columns: ColumnsType<IInvoiceAdminListItem> = useMemo(
    () => [
      {
        title: "S.No.",
        dataIndex: "sno",
        key: "sno",
        width: 60,
        render: (val: number) => <span>{val}</span>,
      },
      {
        title: "Ref Number",
        dataIndex: "number",
        key: "number",
        width: 130,
        render: (text: string) => (
          <Tag color="blue" style={{ fontWeight: 600 }}>
            {text || "N/A"}
          </Tag>
        ),
      },
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        width: "22%",
        render: (comp?: { id: string; name: string }) => (
          <Space>
            <ShopOutlined style={{ color: "#1890ff" }} />
            <Text strong>{comp?.name || "N/A"}</Text>
          </Space>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 110,
        render: (val: string) => (
          <Space>
            <CalendarOutlined style={{ color: "#8c8c8c" }} />
            <span>{val ? dayjs(val).format("DD/MM/YYYY") : "—"}</span>
          </Space>
        ),
      },
      {
        title: "Gross",
        dataIndex: "gross",
        key: "gross",
        width: 100,
        render: (val: number) => (
          <span>£{val != null ? Number(val).toFixed(2) : "0.00"}</span>
        ),
      },
      {
        title: "Due",
        dataIndex: "due",
        key: "due",
        width: 100,
        render: (val: number) => (
          <span
            style={{
              color: val > 0 ? "#ff4d4f" : undefined,
              fontWeight: val > 0 ? 600 : 400,
            }}
          >
            £{val != null ? Number(val).toFixed(2) : "0.00"}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (val: string) => {
          const raw = val || "Draft";
          const s = raw.toLowerCase();
          const isCancelled = s === "void" || s === "cancelled";
          const displayLabel = isCancelled ? "Cancelled" : raw;
          const color =
            s === "paid"
              ? "success"
              : s === "sent"
              ? "processing"
              : s === "overdue"
              ? "error"
              : isCancelled
              ? "default"
              : "default";
          return <Tag color={color}>{displayLabel}</Tag>;
        },
      },
      {
        title: "Practice",
        dataIndex: "practiceId",
        key: "practiceId",
        width: "16%",
        render: (practiceId: number) => (
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <strong>{practiceMap[practiceId] || `Practice ${practiceId}`}</strong>
            <Tag color="purple" style={{ fontSize: 11 }}>
              ID: {practiceId}
            </Tag>
          </span>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 70,
        render: (_, record: IInvoiceAdminListItem) => {
          const isThisDownloading = downloadingId === record.id;
          return (
            <Tooltip
              title={
                isThisDownloading
                  ? "Downloading invoice..."
                  : "Download Invoice PDF"
              }
            >
              <Button
                type="text"
                loading={isThisDownloading}
                icon={
                  !isThisDownloading ? (
                    <DownloadOutlined style={{ color: "#1890ff", fontSize: 16 }} />
                  ) : undefined
                }
                onClick={() => handleSingleDownload(record)}
              />
            </Tooltip>
          );
        },
      },
    ],
    [practiceMap, downloadingId, handleSingleDownload]
  );

  const primaryActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: "extractSelected",
        label: `Selected ${
          selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""
        }`,
        icon: <CloudDownloadOutlined />,
        type: "primary",
        disabled: selectedRowKeys.length === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(false),
      },
      {
        key: "extractAll",
        label: `Extract All (${totalItems})`,
        icon: <CloudDownloadOutlined />,
        type: "default",
        disabled: totalItems === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(true),
      },
    ],
    [selectedRowKeys.length, totalItems, downloading, handleBulkExtraction]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleRowSelectionChange,
    }),
    [selectedRowKeys, handleRowSelectionChange]
  );

  const renderHeaderSubtitle = () => {
    if (!extractionProgress.active) {
      return "Extract single or multiple invoice PDFs";
    }

    return (
      <div style={{ maxWidth: 480, marginTop: 4 }}>
        <Space
          style={{
            marginBottom: 2,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Space>
            <Text strong style={{ fontSize: 13, color: "#1890ff" }}>
              Bulk Extraction Progress: {extractionProgress.percent}%
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({extractionProgress.processed} / {extractionProgress.total} files
              processed)
            </Text>
          </Space>
          <Button
            type="link"
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={handleCancelExtraction}
            style={{ padding: 0 }}
          >
            Cancel
          </Button>
        </Space>
        <Progress
          percent={extractionProgress.percent}
          status="active"
          strokeColor={{ "0%": "#108ee9", "100%": "#52c41a" }}
          size="small"
        />
      </div>
    );
  };

  const now = dayjs();
  const quarterStartMonth = Math.floor(now.month() / 3) * 3;
  const previousQuarterYear = now.month() < 3 ? now.year() - 1 : now.year();
  const previousQuarterStartMonth = now.month() < 3 ? 9 : quarterStartMonth - 3;

  const datePresets: {
    label: string;
    value: [dayjs.Dayjs, dayjs.Dayjs];
  }[] = [
    {
      label: "This Month",
      value: [dayjs().startOf("month"), dayjs().endOf("day")],
    },
    {
      label: "Last Month",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: "This Quarter",
      value: [
        now.startOf("year").month(quarterStartMonth).startOf("month"),
        now.endOf("day"),
      ],
    },
    {
      label: "Last Quarter",
      value: [
        dayjs()
          .year(previousQuarterYear)
          .startOf("year")
          .month(previousQuarterStartMonth)
          .startOf("month"),
        dayjs()
          .year(previousQuarterYear)
          .startOf("year")
          .month(previousQuarterStartMonth + 2)
          .endOf("month"),
      ],
    },
    {
      label: "This Year",
      value: [dayjs().startOf("year"), dayjs().endOf("day")],
    },
    {
      label: "Last Year",
      value: [
        dayjs().subtract(1, "year").startOf("year"),
        dayjs().subtract(1, "year").endOf("year"),
      ],
    },
  ];

  return (
    <PageLayout>
      <PageHeader
        title="Invoice Extraction"
        subtitle={renderHeaderSubtitle()}
        actions={[
          <Select
            key="module-switcher"
            value="invoice"
            onChange={(val) => {
              if (val === "xcelify") {
                if (onSwitch) onSwitch("xcelify");
                else navigate("/xcelify-extraction");
              }
            }}
            options={[
              { value: "xcelify", label: "Xcelify Extraction" },
              { value: "invoice", label: "Invoice Extraction" },
            ]}
            style={{ width: 180 }}
          />,
        ]}
      />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={handleSearch}
            searchPlaceholder="Search Ref No, Company..."
            onRefresh={handleRefresh}
            filterCriteria={[
              {
                key: "clientId",
                label: "Company",
                type: "select" as const,
                placeholder: !selectedPracticeId
                  ? "Select practice first"
                  : loadingCompanies
                  ? "Loading..."
                  : "Select Company",
                options: companyList.map((c) => ({
                  value: c.id,
                  label: c.name,
                })),
              },
              {
                key: "status",
                label: "Status",
                type: "select" as const,
                placeholder: "Select Status",
                options: [
                  { value: "Draft", label: "Draft" },
                  { value: "Sent", label: "Sent" },
                  { value: "Paid", label: "Paid" },
                  { value: "Overdue", label: "Overdue" },
                  { value: "Cancelled", label: "Cancelled" },
                ],
              },
            ] as FilterCriteria[]}
            activeFilters={{
              clientId: selectedClientId,
              status: statusFilter,
            }}
            onFiltersChange={(filters) => {
              handleCompanyChange(filters.clientId);
              handleStatusChange(filters.status);
            }}
            extraBeforeFilters={
              <RangePicker
                allowClear={false}
                format="DD/MM/YYYY"
                presets={datePresets}
                value={[fromDate, toDate]}
                onChange={(dates) => {
                  if (dates) {
                    handleDateRangeChange(dates[0], dates[1]);
                  } else {
                    handleDateRangeChange(null, null);
                  }
                }}
                style={{ width: 250 }}
              />
            }
            extra={
              <Select
                showSearch
                allowClear
                placeholder="Select Practice"
                style={{ width: 200 }}
                value={selectedPracticeId}
                onChange={handlePracticeChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={(allPracticeIds || []).map((p: IPractices) => ({
                  value: p.value,
                  label: `${p.label} (ID: ${p.value})`,
                }))}
              />
            }
          />
        </div>
        <Table
          dataSource={items}
          columns={columns}
          rowSelection={rowSelection}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: "calc(100vh - 270px)" }}
          loading={{
            spinning: loading || downloading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            total: totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setCurrentPage(paging.current);
              setPageSize(paging.pageSize);
            }
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default InvoiceExtraction;
